import { supabase } from '../lib/supabase';
import { errorMonitoringService } from './errorMonitoringService';

/**
 * Performance Monitoring Service
 * Tracks and logs performance metrics for the application
 */

class PerformanceMonitoringService {
  constructor() {
    this.metrics = new Map();
    this.isEnabled = import.meta.env?.VITE_ENABLE_PERFORMANCE_MONITORING === 'true';
    this.reportingInterval = 30000; // 30 seconds
    this.metricsBuffer = [];
    
    if (this.isEnabled) {
      this.startPeriodicReporting();
      this.setupPerformanceObservers();
    }
  }

  /**
   * Start a performance measurement
   */
  startMeasure(label) {
    if (!this.isEnabled) return null;
    
    const measureId = `${label}-${Date.now()}`;
    const startTime = performance.now();
    
    // Start Sentry transaction
    const transaction = errorMonitoringService?.startTransaction(label, 'measure');
    
    return {
      id: measureId,
      label,
      startTime,
      transaction,
      end: () => this.endMeasure(measureId, label, startTime, transaction)
    };
  }

  /**
   * End a performance measurement
   */
  endMeasure(measureId, label, startTime, transaction) {
    if (!this.isEnabled) return null;
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // End Sentry transaction
    if (transaction) {
      transaction?.finish();
    }

    // Log slow operations to Sentry
    if (duration > 1000) {
      errorMonitoringService?.captureMessage(
        `Slow operation detected: ${label}`,
        'warning',
        {
          tags: { performance: 'slow' },
          extra: { duration, label }
        }
      );
    }
    
    const metric = {
      id: measureId,
      label,
      duration,
      timestamp: new Date()?.toISOString(),
      type: 'custom'
    };
    
    this.recordMetric(metric);
    return metric;
  }

  /**
   * Record a metric
   */
  recordMetric(metric) {
    if (!this.isEnabled) return;
    
    // Store in memory
    if (!this.metrics?.has(metric?.label)) {
      this.metrics?.set(metric?.label, []);
    }
    
    const metrics = this.metrics?.get(metric?.label);
    metrics?.push(metric);
    
    // Keep only last 100 metrics per label
    if (metrics?.length > 100) {
      metrics?.shift();
    }
    
    // Add to buffer for batch reporting
    this.metricsBuffer?.push(metric);
  }

  /**
   * Track API call performance
   */
  async trackApiCall(apiName, apiCall) {
    const measure = this.startMeasure(`api-${apiName}`);
    
    try {
      const result = await apiCall();
      measure?.end();
      
      this.recordMetric({
        label: `api-${apiName}`,
        status: 'success',
        timestamp: new Date()?.toISOString(),
        type: 'api'
      });
      
      return result;
    } catch (error) {
      measure?.end();
      
      this.recordMetric({
        label: `api-${apiName}`,
        status: 'error',
        error: error?.message,
        timestamp: new Date()?.toISOString(),
        type: 'api'
      });
      
      throw error;
    }
  }

  /**
   * Track component render performance
   */
  trackComponentRender(componentName, renderFn) {
    if (!this.isEnabled) return renderFn();
    
    const measure = this.startMeasure(`render-${componentName}`);
    const result = renderFn();
    measure?.end();
    
    return result;
  }

  /**
   * Get metrics summary
   */
  getMetricsSummary(label) {
    const metrics = this.metrics?.get(label) || [];
    
    if (metrics?.length === 0) {
      return null;
    }
    
    const durations = metrics?.map(m => m?.duration)?.filter(d => d !== undefined);
    
    if (durations?.length === 0) {
      return null;
    }
    
    return {
      label,
      count: durations?.length,
      min: Math.min(...durations),
      max: Math.max(...durations),
      avg: durations?.reduce((a, b) => a + b, 0) / durations?.length,
      p50: this.calculatePercentile(durations, 50),
      p95: this.calculatePercentile(durations, 95),
      p99: this.calculatePercentile(durations, 99)
    };
  }

  /**
   * Calculate percentile
   */
  calculatePercentile(values, percentile) {
    const sorted = [...values]?.sort((a, b) => a - b);
    const index = Math.ceil((percentile / 100) * sorted?.length) - 1;
    return sorted?.[index];
  }

  /**
   * Get all metrics summaries
   */
  getAllMetricsSummaries() {
    const summaries = {};
    
    for (const label of this.metrics?.keys()) {
      const summary = this.getMetricsSummary(label);
      if (summary) {
        summaries[label] = summary;
      }
    }
    
    return summaries;
  }

  /**
   * Setup performance observers
   */
  setupPerformanceObservers() {
    // Observe long tasks
    if ('PerformanceObserver' in window) {
      try {
        const longTaskObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            this.recordMetric({
              label: 'long-task',
              duration: entry.duration,
              timestamp: new Date().toISOString(),
              type: 'long-task'
            });
          }
        });
        
        longTaskObserver?.observe({ entryTypes: ['longtask'] });
      } catch (e) {
        console.warn('Long task observation not supported:', e);
      }

      // Observe navigation timing
      try {
        const navigationObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            this.recordMetric({
              label: 'navigation',
              duration: entry.duration,
              domContentLoaded: entry.domContentLoadedEventEnd - entry.domContentLoadedEventStart,
              loadComplete: entry.loadEventEnd - entry.loadEventStart,
              timestamp: new Date().toISOString(),
              type: 'navigation'
            });
          }
        });
        
        navigationObserver?.observe({ entryTypes: ['navigation'] });
      } catch (e) {
        console.warn('Navigation observation not supported:', e);
      }

      // Observe resource timing
      try {
        const resourceObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration > 1000) { // Only track slow resources
              this.recordMetric({
                label: 'slow-resource',
                resource: entry.name,
                duration: entry.duration,
                timestamp: new Date().toISOString(),
                type: 'resource'
              });
            }
          }
        });
        
        resourceObserver?.observe({ entryTypes: ['resource'] });
      } catch (e) {
        console.warn('Resource observation not supported:', e);
      }
    }
  }

  /**
   * Start periodic reporting to database
   */
  startPeriodicReporting() {
    setInterval(() => {
      this.reportMetrics();
    }, this.reportingInterval);
  }

  /**
   * Report metrics to database
   */
  async reportMetrics() {
    if (this.metricsBuffer?.length === 0) return;
    
    try {
      const metricsToReport = [...this.metricsBuffer];
      this.metricsBuffer = [];
      
      // Store in Supabase (optional - only if table exists)
      const { error } = await supabase?.from('performance_metrics')?.insert(metricsToReport?.map(m => ({
          label: m?.label,
          duration: m?.duration,
          type: m?.type,
          metadata: {
            status: m?.status,
            error: m?.error,
            resource: m?.resource,
            domContentLoaded: m?.domContentLoaded,
            loadComplete: m?.loadComplete
          },
          created_at: m?.timestamp
        })));
      
      if (error && error?.code !== '42P01') { // Ignore table not found
        console.error('Failed to report metrics:', error);
      }
    } catch (error) {
      console.error('Error reporting metrics:', error);
    }
  }

  /**
   * Track database query performance
   */
  async trackQuery(queryName, queryFn) {
    const measure = this.startMeasure(`query-${queryName}`);
    
    try {
      const result = await queryFn();
      const metric = measure?.end();
      
      // Warn about slow queries
      if (metric && metric?.duration > 1000) {
        console.warn(`Slow query detected: ${queryName} took ${metric?.duration?.toFixed(2)}ms`);
      }
      
      return result;
    } catch (error) {
      measure?.end();
      throw error;
    }
  }

  /**
   * Get performance report
   */
  getPerformanceReport() {
    return {
      summaries: this.getAllMetricsSummaries(),
      recentMetrics: this.getRecentMetrics(),
      slowQueries: this.getSlowQueries(),
      longTasks: this.getLongTasks()
    };
  }

  /**
   * Get recent metrics
   */
  getRecentMetrics(limit = 20) {
    const allMetrics = [];
    
    for (const metrics of this.metrics?.values()) {
      allMetrics?.push(...metrics);
    }
    
    return allMetrics?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))?.slice(0, limit);
  }

  /**
   * Get slow queries
   */
  getSlowQueries(threshold = 1000) {
    const queries = [];
    
    for (const [label, metrics] of this.metrics?.entries()) {
      if (label?.startsWith('query-') || label?.startsWith('api-')) {
        const slowMetrics = metrics?.filter(m => m?.duration && m?.duration > threshold);
        queries?.push(...slowMetrics);
      }
    }
    
    return queries?.sort((a, b) => b?.duration - a?.duration);
  }

  /**
   * Get long tasks
   */
  getLongTasks() {
    return this.metrics?.get('long-task') || [];
  }

  /**
   * Clear metrics
   */
  clearMetrics() {
    this.metrics?.clear();
    this.metricsBuffer = [];
  }
}

// Export singleton instance
export const performanceMonitor = new PerformanceMonitoringService();

// Export utility functions
export const trackApiCall = (name, call) => performanceMonitor?.trackApiCall(name, call);
export const trackQuery = (name, query) => performanceMonitor?.trackQuery(name, query);
export const startMeasure = (label) => performanceMonitor?.startMeasure(label);
export const getPerformanceReport = () => performanceMonitor?.getPerformanceReport();